import { Box, Typography } from '@mui/material';
import ProtectedEmail from '../../../../../ui/protectedEmail';
import PolicyContentSection, {
  SectionTitle,
  SectionContent,
  SubsectionTitle,
  SubsectionText,
  ContactInfo,
  ContactItem,
  AddressBlock,
} from '../../../shared/components/PolicyContentSection';

const ContentSection = () => {
  return (
    <PolicyContentSection
      title="Privacy Policy"
      lastUpdated="Last updated: December 17th, 2025"
      showPolicyHeader={false}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}>
          (For website visitors and online interactions)
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'text.secondary' }}
        >
          <strong>Note:</strong> This Privacy Policy explains how we handle information collected
          through our website and related online tools. For information about how we protect client
          health information (PHI) in connection with services, please see our Client Privacy Notice
          (HIPAA).
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>1. Who We Are</SectionTitle>
        <SectionContent>
          Nirva – Interpersonal Hypnotherapy Clinic ("Nirva," "we," "our," or "us") operates the
          website at mynirva.com and related pages. This policy explains how we collect, use, and
          protect information when you visit our site or interact with us online.
        </SectionContent>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>2. Information We Collect</SectionTitle>
        <SectionContent>We may collect:</SectionContent>
        <SubsectionTitle>Information you provide directly, such as:</SubsectionTitle>
        <SubsectionText>
          • Name and contact details when you complete a form or subscribe to updates
        </SubsectionText>
        <SubsectionText>
          • Information you send in messages or emails (we ask that you keep health details limited
          via web forms and email)
        </SubsectionText>

        <SubsectionTitle>Information collected automatically, such as:</SubsectionTitle>
        <SubsectionText>• IP address and browser type</SubsectionText>
        <SubsectionText>• Device type and operating system</SubsectionText>
        <SubsectionText>• Pages visited and time spent on the site</SubsectionText>
        <SubsectionText>• Referring website or campaign</SubsectionText>
        <SubsectionText>• General location (city/region) based on IP</SubsectionText>
        <SubsectionText>
          This may be collected using cookies, pixels, or similar technologies.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>3. Cookies & Similar Technologies</SectionTitle>
        <SectionContent>We may use cookies or similar tools to:</SectionContent>
        <SubsectionText>• Help the site function properly</SubsectionText>
        <SubsectionText>• Understand how visitors use the site</SubsectionText>
        <SubsectionText>• Improve content and user experience</SubsectionText>
        <SubsectionText>
          • Measure the reach of our educational and marketing efforts
        </SubsectionText>
        <SubsectionText>
          You can typically control cookies through your browser settings. If you disable cookies,
          some features may not work as intended.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>4. How We Use Website Information</SectionTitle>
        <SectionContent>We may use your information to:</SectionContent>
        <SubsectionText>• Respond to your inquiries and messages</SubsectionText>
        <SubsectionText>
          • Provide information you request about Nirva or our services
        </SubsectionText>
        <SubsectionText>• Improve the site's performance, security, and content</SubsectionText>
        <SubsectionText>
          • Analyze usage trends and measure the effectiveness of our outreach
        </SubsectionText>
        <SubsectionText>• Comply with legal obligations and enforce our policies</SubsectionText>
        <SubsectionText>We do not sell your personal information.</SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>5. When We Share Information</SectionTitle>
        <SectionContent>We may share website-related information with:</SectionContent>
        <SubsectionText>
          • Vendors and service providers who help us operate the site, send communications, or
          analyze usage (for example, hosting providers, analytics tools). These vendors are
          required to protect your information and use it only for services they provide to us.
        </SubsectionText>
        <SubsectionText>
          • Legal or regulatory authorities when required by law, regulation, court order, or to
          protect our rights or the rights and safety of others.
        </SubsectionText>
        <SubsectionText>
          • For clinical client PHI, we use Business Associate Agreements (BAAs) with HIPAA-eligible
          vendors as required and follow our Client Privacy Notice.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>6. Email & Communications</SectionTitle>
        <SectionContent>If you provide your email address, we may contact you with:</SectionContent>
        <SubsectionText>• Responses to your requests</SubsectionText>
        <SubsectionText>• Information about Nirva and our services</SubsectionText>
        <SubsectionText>• Occasional updates, newsletters, or invitations</SubsectionText>
        <SubsectionText>
          You can opt out of non-essential emails by following the unsubscribe link or by contacting
          us at{' '}
          <ProtectedEmail email="support@mynirva.com" />
          . We may still send important administrative or service-related messages that are not
          promotional.
        </SubsectionText>
        <SubsectionText>
          We ask that you do not send detailed health information via regular email. For sensitive
          matters, we use secure methods or discuss in session.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>7. How We Protect Information</SectionTitle>
        <SectionContent>
          We use reasonable administrative, technical, and physical safeguards to protect
          information collected through our website, including:
        </SectionContent>
        <SubsectionText>• Secure hosting and encryption where appropriate</SubsectionText>
        <SubsectionText>• Access controls and authentication</SubsectionText>
        <SubsectionText>• Regular updates and monitoring</SubsectionText>
        <SubsectionText>
          However, no system is 100% secure. By using the site, you understand that there is some
          risk when transmitting information online.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>8. Your Choices & Rights</SectionTitle>
        <SectionContent>Depending on your location, you may have rights to:</SectionContent>
        <SubsectionText>• Access, correct, or delete certain personal information</SubsectionText>
        <SubsectionText>• Restrict or object to certain types of processing</SubsectionText>
        <SubsectionText>• Withdraw consent for communications</SubsectionText>
        <SubsectionText>
          To make a request, contact us at{' '}
          <ProtectedEmail email="privacy@mynirva.com" />
          . We may need to verify your identity before acting on your request.
        </SubsectionText>
        <SubsectionText>
          For rights related specifically to health information and PHI, see our Client Privacy
          Notice.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>9. Children's Privacy</SectionTitle>
        <SectionContent>
          Our website is not intended for children under 13. We do not knowingly collect personal
          information from children under 13 without appropriate parental consent. If you believe a
          child has provided us information without consent, please contact us so we can address it.
        </SectionContent>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>10. International Visitors</SectionTitle>
        <SectionContent>
          Nirva is based in the United States. By using our website from another country, you
          understand that your information may be transferred to and processed in the U.S., where
          privacy laws may differ from those in your country.
        </SectionContent>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionTitle>11. Changes to This Privacy Policy</SectionTitle>
        <SectionContent>
          We may update this Website Privacy Policy from time to time. When we do, we will update
          the "Last updated" date at the top of this page. Significant changes may also be
          highlighted on the site.
        </SectionContent>
        <SubsectionText>
          Your continued use of the website after changes are posted means you accept the updated
          policy.
        </SubsectionText>
      </Box>

      <Box sx={{ mb: 6 }}>
        <SectionTitle>12. Contact Us</SectionTitle>
        <SectionContent>For questions or requests related to this Privacy Policy:</SectionContent>
        <ContactInfo>
          <ContactItem>
            General support:{' '}
            <ProtectedEmail email="support@mynirva.com" />
          </ContactItem>
          <ContactItem>
            Privacy & Security Officer:{' '}
            <ProtectedEmail email="privacy@mynirva.com" />
          </ContactItem>
          <ContactItem>Mailing address:</ContactItem>
          <AddressBlock>
            Nirva – Interpersonal Hypnotherapy Clinic
            <br />
            540 E. Horatio Avenue, Suite 200
            <br />
            Maitland, FL 32751
          </AddressBlock>
        </ContactInfo>
      </Box>
    </PolicyContentSection>
  );
};

export default ContentSection;
